import humps from "humps";

import { getRailsClient } from "../client";

// Helper to convert camelCase to snake_case with proper number handling
const toSnakeCase = (str: string): string => {
  return humps
    .decamelize(str)
    .replace(/([a-z])(\d)/gi, "$1_$2") // Add underscore before numbers
    .replace(/(\d)([a-z])/gi, "$1_$2"); // Add underscore after numbers
};

export interface LeaderboardParams {
  page?: number;
  query?: string;
  recordsPerPage?: number;
  direction?: string;
  sortBy?: string;
}

export interface LeaderboardResponse {
  users: LeaderboardUser[];
  page: number;
  pagesNumber: number;
}

export interface LeaderboardUser {
  active: boolean;
  currentBelt: string;
  currentBeltImage: string | null;
  gravatarId: string;
  lastActive: string;
  name: string;
  passedRoleImageUrl: string | null;
  ranking: number;
  rating: number;
  thumbImageUrl: string;
  totalPoints: number;
  pointsPast30Days: number;
  weeklyStreak: number;
  company: string | null;
  jobRole: string | null;
  country: string | null;
}

// Fetches leaderboard data with pagination, search, and sorting
export const getLeaderboard = async ({
  page = 1,
  query = "",
  recordsPerPage = 10,
  direction,
  sortBy = "",
}: LeaderboardParams = {}): Promise<LeaderboardResponse> => {
  const API = `/api/internal_api/v1/leaderboard`;

  try {
    const params: {
      page: number;
      per_page: number;
      query?: string;
      direction?: string;
      sort_by?: string;
    } = {
      page,
      per_page: recordsPerPage,
    };

    if (query) {
      params.query = query;
    }

    if (direction) {
      params.direction = direction;
    }

    if (sortBy) {
      params.sort_by = toSnakeCase(sortBy);
    }

    const { data } = await getRailsClient().get(API, { params });

    return {
      users: data.users || [],
      page: data.page || page,
      pagesNumber: data.pagesNumber || 1,
    };
  } catch (error) {
    console.error("Error fetching leaderboard:", error);
    throw error;
  }
};

// Downloads the full leaderboard as a CSV file
export const downloadLeaderboard = async (): Promise<void> => {
  const API = `/api/internal_api/v1/leaderboard/download`;
  const { data } = await getRailsClient().get(API);
  const blob = new Blob([data], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.setAttribute("href", url);
  link.setAttribute("download", "leaderboard-export.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
